import { memo } from 'react';
import { Link } from 'react-router-dom';

import { Instagram, MapPinned } from 'lucide-react';

const footerLinks = {
    Explore: [
        {
            label: 'Treks',
            to: '/treks',
        },
        {
            label: 'Regions',
            to: '/regions',
        },
        {
            label: 'Beginner Treks',
            to: '/treks?difficulty=Easy',
        },
    ],

    Company: [
        {
            label: 'About Us',
            to: '/about',
        },
        {
            label: 'Contact',
            to: '/contact',
        },
        {
            label: 'Partner With Us',
            to: '/partner-with-us',
        },
    ],

    Support: [
        {
            label: 'FAQ',
            to: '/faqs',
        },
        {
            label: 'Help Center',
            to: '/help-center',
        },
        {
            label: 'Report an Issue',
            to: '/report-issue',
        },
    ],

    Legal: [
        {
            label: 'Privacy Policy',
            to: '/privacy-policy',
        },
        {
            label: 'Terms & Conditions',
            to: '/terms-of-use',
        },
        {
            label: 'Enquiry & Operator Policy',
            to: '/cancellation-refund-policy',
        },
        {
            label: 'Cookie Policy',
            to: '/cookie-policy',
        },
        {
            label: 'Disclaimer',
            to: '/disclaimer',
        },
    ],
};

const socials = [
    {
        href: 'https://www.instagram.com/FindMeTrek/',
        label: 'Instagram',
        icon: <Instagram size={18} />,
    },
];

const Footer = () => {
    return (
        <footer
            className="
                relative overflow-hidden
                bg-[#071019]
                text-white
            "
            role="contentinfo"
            aria-label="Site footer"
        >

            {/* BG GLOW */}
            <div
                className="
                    absolute left-1/2 top-0
                    h-[500px] w-[500px]
                    -translate-x-1/2
                    rounded-[20px]
                    bg-emerald-500/10
                    blur-3xl
                "
            />

            {/* MAIN FOOTER */}
            <div
                className="
                    relative z-10
                    mx-auto
                    max-w-8xl
                    px-6
                    py-16
                    lg:px-28
                "
            >

                <div
                    className="
                    grid gap-5 sm:gap-14
                    lg:grid-cols-[1fr_.5fr_.5fr_.5fr_.5fr]
                    "
                >

                    {/* BRAND */}
                    <div>

                        {/* LOGO */}
                        <Link
                            to="/"
                            className="
                                inline-flex items-center gap-3
                            "
                        >

                            <img src="/logo.png" alt="FindMeTrek" className="w-56" />
                        </Link>

                        {/* DESC */}
                        <p
                            className="
                                mt-6
                                max-w-md
                                text-sm
                                leading-8
                                text-white/65
                            "
                        >
                            Helping trekkers discover
                            unforgettable experiences
                            across India. Plan smarter,
                            trek safer, and explore
                            with confidence.
                        </p>

                        {/* SOCIALS */}
                        <div className="mt-8 flex gap-3">

                            {socials.map(
                                ({
                                     href,
                                     label,
                                     icon,
                                 }) => (
                                    <a
                                        key={
                                            label
                                        }
                                        href={
                                            href
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={
                                            label
                                        }
                                        className="
                                            flex h-12 w-12
                                            items-center justify-center
                                            rounded-[20px]
                                            border border-white/10
                                            bg-white/5
                                            text-white/70
                                            backdrop-blur-xl
                                            transition-all duration-200
                                            hover:-translate-y-1
                                            hover:bg-white
                                            hover:text-black
                                        "
                                    >
                                        {
                                            icon
                                        }
                                    </a>
                                ),
                            )}
                        </div>
                    </div>

                    {/* LINKS */}
                    {Object.entries(
                        footerLinks,
                    ).map(
                        (
                            [
                                title,
                                links,
                            ],
                        ) => (
                            <div
                                key={
                                    title
                                }
                            >

                                <h4
                                    className="
                                        flex items-center gap-2
                                        text-sm
                                        font-semibold
                                        uppercase tracking-[0.18em]
                                        text-emerald-300
                                    "
                                >
                                    <MapPinned
                                        size={
                                            14
                                        }
                                    />
                                    {title}
                                </h4>

                                <ul className="mt-6 space-y-4">

                                    {links.map(
                                        (
                                            item,
                                        ) => (
                                            <li
                                                key={
                                                    item.label
                                                }
                                            >
                                                <Link
                                                    className="
                                                        group inline-flex items-center gap-2
                                                        text-sm
                                                        text-white/65
                                                        transition
                                                        hover:text-white
                                                        min-h-[44px]
                                                        py-1.5
                                                    "
                                                    to={
                                                        item.to
                                                    }
                                                >
                                                    <span
                                                        className="
                                                            h-1.5 w-1.5
                                                            rounded-[20px]
                                                            bg-white/20
                                                            transition
                                                            group-hover:bg-emerald-300
                                                        "
                                                    />

                                                    {
                                                        item.label
                                                    }
                                                </Link>
                                            </li>
                                        ),
                                    )}
                                </ul>
                            </div>
                        ),
                    )}
                </div>

                {/* BOTTOM */}
                <div
                    className="
                        mt-16
                        flex flex-col gap-4
                        border-t border-white/10
                        pt-6
                        text-sm text-white/45
                        lg:flex-row
                        lg:items-center
                        lg:justify-between
                    "
                >

                    <p>
                        &copy; 2026 FindMeTrek.
                        All rights reserved.
                    </p>

                    <div className="flex flex-wrap gap-2">

                        <Link
                            to="/privacy-policy"
                            className="
                                transition
                                hover:text-white
                                min-h-[44px]
                                inline-flex items-center px-3
                            "
                        >
                            Privacy
                        </Link>

                        <Link
                            to="/terms-of-use"
                            className="
                                transition
                                hover:text-white
                                min-h-[44px]
                                inline-flex items-center px-3
                            "
                        >
                            Terms
                        </Link>

                        <Link
                            to="/cancellation-refund-policy"
                            className="
                                transition
                                hover:text-white
                                min-h-[44px]
                                inline-flex items-center px-3
                            "
                        >
                            Cancellation & Refunds
                        </Link>

                        <Link
                            to="/cookie-policy"
                            className="
                                transition
                                hover:text-white
                                min-h-[44px]
                                inline-flex items-center px-3
                            "
                        >
                            Cookies
                        </Link>

                        <Link
                            to="/disclaimer"
                            className="
                                transition
                                hover:text-white
                                min-h-[44px]
                                inline-flex items-center px-3
                            "
                        >
                            Disclaimer
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default memo(Footer);
